import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthProvider";
import api from "../api/api";
import { setInstalledApps } from "../api/api";

const InstalledAppsContext = createContext();

/**
 * InstalledAppsProvider
 *
 * Centralised context that tracks which modules are installed for the
 * current tenant.  Both the Sidebar (menu filtering) and ProtectedRoute
 * (route-level gating) consume this — guaranteeing a single source of truth
 * and eliminating duplicate `/apps/my-apps/` calls.
 *
 * Auto-refreshes when:
 *   • the user logs in (token changes)
 *   • an install/uninstall triggers the existing `company-updated` event
 */
const InstalledAppsProvider = ({ children }) => {
  const { token } = useAuth();
  const [installedApps, setInstalledApps] = useState(new Set());
  const [appTrialStatuses, setAppTrialStatuses] = useState(new Map());
  const [aiEnabled, setAiEnabled] = useState(false);
  const [isAppsLoading, setIsAppsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isFetchingRef = useRef(false);

  const fetchInstalledApps = useCallback(async (forceRefresh = false) => {
    if (!token) {
      setInstalledApps(new Set());
      setIsAppsLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current && !forceRefresh) {
      return;
    }

    isFetchingRef.current = true;

    // Show loading state during refresh to ensure UI updates
    if (forceRefresh) {
      setIsAppsLoading(true);
    }

    try {
      const tenantSchema = localStorage.getItem('tenant_schema');
      const headers = tenantSchema ? { 'X-Tenant-Id': tenantSchema } : {};

      const [response, featuresRes] = await Promise.all([
        api.get("/apps/my-apps/", { headers }),
        // Tenant feature flags (AI premium switch). Non-fatal: on error keep
        // the previous value — the backend enforces the flag regardless.
        api.get("/apps/my-apps/features/", { headers }).catch(() => null),
      ]);
      const apps = response.data.results || response.data;
      const appNames = new Set(apps.map((item) => item.app.technical_name));
      const trialStatuses = new Map(
        apps.map((item) => [item.app.technical_name, item.trial_status || "none"])
      );

      setInstalledApps(appNames);
      setAppTrialStatuses(trialStatuses);
      if (featuresRes) setAiEnabled(!!featuresRes.data?.ai_enabled);
      setLastUpdated(new Date());

      if (process.env.NODE_ENV === 'development') {
        console.log('[InstalledApps] Refreshed installed apps:', Array.from(appNames));
      }
    } catch (error) {
      console.error('[InstalledApps] Error fetching installed apps:', error);
    } finally {
      isFetchingRef.current = false;
      setIsAppsLoading(false);
    }
  }, [token]);

  // Fetch on mount and when token changes (login / logout)
  useEffect(() => {
    setIsAppsLoading(true);
    fetchInstalledApps(true);
  }, [fetchInstalledApps]);

  // Re-fetch when an app is installed / uninstalled via the AppStore or
  // SaaSAdmin pages (they dispatch this custom event).
  useEffect(() => {
    const handleUpdate = (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[InstalledApps] Received company-updated event:', event.detail);
      }

      // Force a fresh fetch to ensure we get the latest data
      fetchInstalledApps(true);
    };

    window.addEventListener("company-updated", handleUpdate);
    return () => window.removeEventListener("company-updated", handleUpdate);
  }, [fetchInstalledApps]);

  /**
   * Check whether a module is installed for the current tenant.
   * @param {string} appId  — technical_name of the module (e.g. 'helpdesk')
   * @returns {boolean}
   */
  const isAppInstalled = useCallback(
    (appId) => {
      if (!appId) return true; // No requirement → always allowed
      const isInstalled = installedApps.has(appId);

      if (process.env.NODE_ENV === 'development' && !isInstalled) {
        console.log(`[InstalledApps] Access denied: ${appId} not in installed apps:`, Array.from(installedApps));
      }

      return isInstalled;
    },
    [installedApps]
  );

  /**
   * Check whether a module's trial has expired for the current tenant.
   * Backend enforces this on every request (403 module_trial_expired);
   * this only drives menu hiding and the /trial-expired route.
   * @param {string} appId — technical_name of the module (e.g. 'nexus')
   * @returns {boolean}
   */
  const isAppTrialExpired = useCallback(
    (appId) => {
      if (!appId) return false;
      return appTrialStatuses.get(appId) === "expired";
    },
    [appTrialStatuses]
  );

  const contextValue = useMemo(
    () => ({
      installedApps,
      isAppInstalled,
      isAppTrialExpired,
      aiEnabled,
      isAppsLoading,
      refreshInstalledApps: () => fetchInstalledApps(true),
      lastUpdated,
    }),
    [installedApps, isAppInstalled, isAppTrialExpired, aiEnabled, isAppsLoading, fetchInstalledApps, lastUpdated]
  );

  return (
    <InstalledAppsContext.Provider value={contextValue}>
      {children}
    </InstalledAppsContext.Provider>
  );
};

export const useInstalledApps = () => {
  return useContext(InstalledAppsContext);
};

/**
 * Convenience hook for the tenant's premium AI flag.
 * Backend enforces the flag on every AI request; this only drives UI
 * hiding/disabling so users never hit a dead button.
 */
export const useAiEnabled = () => {
  const ctx = useContext(InstalledAppsContext);
  return { aiEnabled: !!ctx?.aiEnabled, isLoading: !!ctx?.isAppsLoading };
};

export default InstalledAppsProvider;
